import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { StripeApi } from '../../api/stripe.api';
import {
  PaymentMethodsActions,
  PaymentMethodsApiActions,
} from './payment-methods.actions';
import { PaymentMethod } from '../../models/payment-method.model';
import * as errorHelpers from 'src/app/helpers/error.helpers';
import { ToasterService } from 'app/services/toaster.service';

@Injectable()
export class PaymentMethodsEffects {
  constructor(
    private api: StripeApi,
    private actions$: Actions,
    private toasterService: ToasterService,
    private translateService: TranslateService
  ) { }

  loadPaymentMethods$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PaymentMethodsApiActions.fetchPaymentMethods),
      switchMap(() => {
        return this.api.getPaymentMethods$().pipe(
          map((paymentMethods: PaymentMethod[]) => {
            return PaymentMethodsApiActions.retrievedPaymentMethods({
              paymentMethods,
            });
          }),
          catchError((err) => {
            return of(
              PaymentMethodsApiActions.failed({
                errorMessage: errorHelpers.getErrorMessage(
                  err,
                  this.translateService
                ),
              })
            );
          })
        );
      })
    );
  });

  postPaymentMethod$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PaymentMethodsApiActions.postPaymentMethod),
      switchMap((action) => {
        return this.api
          .postPaymentMethod$({ paymentId: action.paymentId })
          .pipe(
            map((paymentMethod: PaymentMethod) => {
              return PaymentMethodsApiActions.postedPaymentMethod({
                paymentMethod,
              });
            }),
            catchError((err) => {
              return of(
                PaymentMethodsApiActions.failed({
                  errorMessage: errorHelpers.getErrorMessage(
                    err,
                    this.translateService
                  ),
                })
              );
            })
          );
      })
    );
  });

  addPaymentMethodsAfterPosted$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PaymentMethodsApiActions.postedPaymentMethod),
      map((action) => {
        return PaymentMethodsActions.addPaymentMethod({
          paymentMethod: action.paymentMethod,
        });
      })
    );
  });

  displayErrorMessage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(PaymentMethodsApiActions.failed),
        tap((action) => {
          if (!!action.errorMessage) {
            this.toasterService.error(null, action.errorMessage);
          }
        })
      );
    },
    { dispatch: false }
  );
}
